'use client';
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-confusing-void-expression */

import type { MutateOptions, UseMutationResult } from '@tanstack/react-query';
import { isDevelopmentRuntime } from '@/lib/runtime-flags';

const debugMutationStatus = isDevelopmentRuntime;

function defaultErrorMapper<TError>(error: TError): Error {
  if (error instanceof Error) {
    return error;
  }

  let fallback = 'Mutation failed';
  if (typeof error === 'string') {
    fallback = error;
  } else if (typeof (error as { message?: unknown })?.message === 'string') {
    fallback = String((error as { message?: unknown }).message);
  }

  return new Error(fallback);
}

function wrapSuccessCallback<TParsed, TRaw, TVariables, TContext>(
  select: (raw: TRaw) => TParsed,
  callback?: MutateOptions<TParsed, Error, TVariables, TContext>['onSuccess']
) {
  if (!callback) {
    return;
  }

  return (rawResult: TRaw, ...rest: unknown[]) => {
    const parsed = select(rawResult);
    (callback as (...arguments_: unknown[]) => void)(parsed, ...rest);
  };
}

function wrapErrorCallback<TError, TVariables, TContext>(
  mapError: (error: TError) => Error,
  callback?: MutateOptions<unknown, Error, TVariables, TContext>['onError']
) {
  if (!callback) {
    return;
  }

  return (rawError: TError | null, ...rest: unknown[]) => {
    const normalized = mapError(rawError ?? ({} as TError));
    (callback as (...arguments_: unknown[]) => void)(normalized, ...rest);
  };
}

function wrapSettledCallback<TParsed, TRaw, TError, TVariables, TContext>(
  select: (raw: TRaw) => TParsed,
  mapError: (error: TError) => Error,
  callback?: MutateOptions<TParsed, Error, TVariables, TContext>['onSettled']
) {
  if (!callback) {
    return;
  }

  return (rawResult: TRaw | undefined, rawError: TError | null, ...rest: unknown[]) => {
    const parsed = rawResult === undefined ? undefined : select(rawResult);
    const normalizedError = rawError == null ? rawError : mapError(rawError);
    (callback as (...arguments_: unknown[]) => void)(parsed, normalizedError, ...rest);
  };
}

function wrapMutateOptions<TParsed, TRaw, TError, TVariables, TContext>(
  options: MutateOptions<TParsed, Error, TVariables, TContext> | undefined,
  select: (raw: TRaw) => TParsed,
  mapError: (error: TError) => Error,
  lifecycle?: {
    onSuccess?: () => void;
    onError?: () => void;
    onSettled?: (rawResult: TRaw | undefined, rawError: TError | null) => void;
  }
): MutateOptions<TRaw, TError, TVariables, TContext> | undefined {
  if (!options) {
    return;
  }

  const { onSuccess, onError, onSettled, ...rest } = options;

  const wrappedSuccess = wrapSuccessCallback(select, onSuccess);
  const wrappedError = wrapErrorCallback(mapError, onError);
  const wrappedSettled = wrapSettledCallback(select, mapError, onSettled);

  const nextOptions: MutateOptions<TRaw, TError, TVariables, TContext> = { ...rest };

  if (wrappedSuccess || lifecycle?.onSuccess) {
    nextOptions.onSuccess = (rawResult: TRaw, ...restArguments: unknown[]) => {
      lifecycle?.onSuccess?.();
      wrappedSuccess?.(rawResult, ...restArguments);
    };
  }

  if (wrappedError || lifecycle?.onError) {
    nextOptions.onError = (rawError: TError, ...restArguments: unknown[]) => {
      lifecycle?.onError?.();
      wrappedError?.(rawError, ...restArguments);
    };
  }

  if (wrappedSettled || lifecycle?.onSettled) {
    nextOptions.onSettled = (rawResult: TRaw | undefined, rawError: TError | null, ...restArguments: unknown[]) => {
      lifecycle?.onSettled?.(rawResult, rawError);
      wrappedSettled?.(rawResult, rawError, ...restArguments);
    };
  }

  return nextOptions;
}

/**
 * Wraps a tRPC mutation so callers can rely on typed/select-ed data and normalized errors
 * without re-implementing boilerplate in every hook that consumes `useMutation`.
 */

export function wrapTrpcMutation<TRaw, TError, TVariables, TContext, TParsed>(
  base: TrpcMutationResult<TRaw, TError, TVariables, TContext>,
  select: (raw: TRaw) => TParsed,
  mapError: (error: TError) => Error = defaultErrorMapper
): WrappedMutationResult<TParsed, TVariables, TContext> {
  return wrapTrpcMutationInternal(base, select, mapError);
}

function wrapTrpcMutationInternal<TRaw, TError, TVariables, TContext, TParsed>(
  base: TrpcMutationResult<TRaw, TError, TVariables, TContext>,
  select: (raw: TRaw) => TParsed,
  mapError: (error: TError) => Error
): WrappedMutationResult<TParsed, TVariables, TContext> {
  type MutationOptions = MutateOptions<TParsed, Error, TVariables, TContext>;

  const baseMutate = base.mutate.bind(base);
  const baseMutateAsync = base.mutateAsync.bind(base);

  const trpcMeta = base.trpc ?? null;
  const mutationLabel =
    (() => {
      if (!trpcMeta) {
        return;
      }
      const pathValue = trpcMeta.path;
      return typeof pathValue === 'string' && pathValue.length > 0 ? pathValue : undefined;
    })() ?? 'unknown';

  const debugLog = (phase: string, payload?: unknown) => {
    if (!debugMutationStatus) {
      return;
    }
    if (payload === undefined) {
      // eslint-disable-next-line no-console
      console.log(`[wrapTrpcMutation:${mutationLabel}] ${phase}`);
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`[wrapTrpcMutation:${mutationLabel}] ${phase}`, payload);
  };

  const initialStatus = base.status ?? 'idle';
  let lastStatus = initialStatus;
  let lastIsSuccess = base.isSuccess ?? initialStatus === 'success';
  let lastIsError = base.isError ?? initialStatus === 'error';
  let lastIsPending = base.isPending ?? initialStatus === 'pending';

  const markStatus = (status: UseMutationResult<TParsed, Error, TVariables, TContext>['status']) => {
    lastStatus = status;
    lastIsSuccess = status === 'success';
    lastIsError = status === 'error';
    lastIsPending = status === 'pending';
  };

  const updateFromBase = () => {
    const status = base.status;
    if (typeof status === 'string' && status !== 'idle') {
      markStatus(status);
    }

    const isSuccess = base.isSuccess;
    if (typeof isSuccess === 'boolean') {
      lastIsSuccess = isSuccess;
    }

    const isError = base.isError;
    if (typeof isError === 'boolean') {
      lastIsError = isError;
    }

    const isPending = base.isPending;
    if (typeof isPending === 'boolean') {
      lastIsPending = isPending;
    }
  };

  const mutateImpl = (variables?: TVariables, options?: MutationOptions) => {
    debugLog('mutate', previewVariables(variables));
    markStatus('pending');
    debugLog('status -> pending');
    baseMutate(
      variables as TVariables,
      wrapMutateOptions(options, select, mapError, {
        onSettled: (rawResult, rawError) => {
          if (rawResult !== undefined && rawError === undefined) {
            markStatus('success');
            debugLog('status -> success');
          } else if (rawError !== undefined) {
            markStatus('error');
            debugLog('status -> error', rawError);
          }
        },
      })
    );
  };

  const mutateAsyncImpl = async (variables?: TVariables, options?: MutationOptions): Promise<TParsed> => {
    debugLog('mutateAsync', previewVariables(variables));
    markStatus('pending');
    debugLog('status -> pending');
    try {
      const raw = await baseMutateAsync(variables as TVariables, wrapMutateOptions(options, select, mapError));
      markStatus('success');
      debugLog('status -> success');
      return select(raw as TRaw);
    } catch (rawError) {
      markStatus('error');
      debugLog('status -> error', rawError);
      throw mapError(rawError as TError);
    }
  };

  const proxy = new Proxy<TrpcMutationResult<TRaw, TError, TVariables, TContext>>(base, {
    get(target, property) {
      if (property !== 'mutate' && property !== 'mutateAsync') {
        updateFromBase();
      }
      if (property === 'mutate') {
        return mutateImpl;
      }

      if (property === 'mutateAsync') {
        return mutateAsyncImpl;
      }

      if (property === 'isSuccess') {
        return lastIsSuccess;
      }

      if (property === 'isError') {
        return lastIsError;
      }

      if (property === 'isPending') {
        return lastIsPending;
      }

      if (property === 'isIdle') {
        return lastStatus === 'idle';
      }

      if (property === 'status') {
        return lastStatus;
      }

      if (property === 'reset') {
        const baseReset = target.reset;
        if (typeof baseReset !== 'function') {
          return baseReset;
        }
        return () => {
          baseReset.call(target);
          markStatus('idle');
        };
      }

      if (property === 'data') {
        const raw = target.data;
        return raw === undefined ? undefined : select(raw as TRaw);
      }

      if (property === 'error') {
        const rawError = target.error;
        return rawError === undefined ? null : mapError(rawError as TError);
      }

      return Reflect.get(target, property) as TrpcMutationResult<
        TRaw,
        TError,
        TVariables,
        TContext
      >[keyof TrpcMutationResult<TRaw, TError, TVariables, TContext>];
    },
    set(target, property, value) {
      if (property === 'mutate' || property === 'mutateAsync') {
        return false;
      }

      return Reflect.set(target, property, value, target);
    },
    has(target, property) {
      if (property === 'data' || property === 'error') {
        return true;
      }

      return Reflect.has(target, property);
    },
  });

  return proxy as unknown as WrappedMutationResult<TParsed, TVariables, TContext>;
}

function previewVariables(input: unknown): unknown {
  if (!debugMutationStatus) {
    return;
  }
  if (input === null || typeof input !== 'object') {
    return input;
  }
  if (Array.isArray(input)) {
    return input.slice(0, 3);
  }
  const record = input as Record<string, unknown>;
  const limited = Object.entries(record).slice(0, 4);
  if (limited.length <= 4 && limited.length === Object.keys(record).length) {
    return record;
  }
  return Object.fromEntries([...limited, ['...', `${Math.max(0, Object.keys(record).length - 4)} more keys`]]);
}

type TrpcMetaPath = {
  path?: string;
};

type TrpcMutationResult<TRaw, TError, TVariables, TContext> = UseMutationResult<TRaw, TError, TVariables, TContext> & {
  trpc?: TrpcMetaPath | null;
};

type MutationOptionsType<TParsed, TVariables, TContext> = MutateOptions<TParsed, Error, TVariables, TContext>;

type MutateFunctionOverrides<TParsed, TVariables, TContext> = [TVariables] extends [undefined]
  ? {
      mutate: (variables?: TVariables, options?: MutationOptionsType<TParsed, TVariables, TContext>) => void;
      mutateAsync: (
        variables?: TVariables,
        options?: MutationOptionsType<TParsed, TVariables, TContext>
      ) => Promise<TParsed>;
    }
  : {
      mutate: (variables: TVariables, options?: MutationOptionsType<TParsed, TVariables, TContext>) => void;
      mutateAsync: (
        variables: TVariables,
        options?: MutationOptionsType<TParsed, TVariables, TContext>
      ) => Promise<TParsed>;
    };

type WrappedMutationResult<TParsed, TVariables, TContext> = Omit<
  TrpcMutationResult<TParsed, Error, TVariables, TContext>,
  'mutate' | 'mutateAsync'
> &
  MutateFunctionOverrides<TParsed, TVariables, TContext>;
